import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Logo from '../Logo/Logo';
import BrandName from '../BrandName/BrandName';
import backgroundImage from '../../backgroundImages/peaceful-nature-scene-with-animals-deer-in-forest-.jpg';

const SplashScreen = ({ onComplete }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = 4000; // 4 seconds
    const interval = 50; // Update every 50ms
    const steps = duration / interval;
    const increment = 100 / steps;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 200); // Small delay before transitioning
          return 100;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
      {/* Background Image - Peaceful Nature Scene */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt={t('nature_background_alt', 'Nature background')}
          className="w-full h-full object-cover opacity-30"
        />
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-8">
        {/* App Logo with initial spin animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          className="mb-6"
        >
          <Logo
            className="w-44 h-44 sm:w-52 sm:h-52 md:w-56 md:h-56 drop-shadow-lg"
            tmClassName="-bottom-0.5 -right-1.5 text-[0.55rem] sm:text-[0.65rem]"
          />
        </motion.div>

        {/* App Name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-center mb-2"
        >
          <h1 
            className="text-3xl sm:text-4xl font-bold text-[#2d5016] tracking-tight drop-shadow-sm"
            style={{ fontFamily: "Georgia, serif" }}
          >
            <BrandName />
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="text-sm sm:text-base text-[#2d5016]/70 mb-8 text-center max-w-xs"
        >
          Connecting animals & communities worldwide.
        </motion.p>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "12rem" }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="h-1.5 bg-gray-200 rounded-full overflow-hidden"
        >
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </motion.div>

        {/* Loading text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-3 text-xs text-[#2d5016]/60"
        >
          Loading...
        </motion.p>
      </div>
    </div>
  );
};

export default SplashScreen;
